import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import UserSideNav from "../../components/user/UserSideNav";
import UserHeader from "../../components/user/UserHeader";
import Modal from "../../components/shared/Modal";
import SmartDataTable from "../../components/shared/SmartDataTable";
import {
  CalendarDays, CheckCircle2, CreditCard, DollarSign, FileClock,
  Loader2, Plus, Ticket, Trophy, Users,
} from "lucide-react";
import { paymentsApi, poolsApi, ticketRequestsApi } from "../../services/api";

function fmtDate(d) {
  return d
    ? new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
    : "-";
}

function fmt(n) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n || 0);
}

function statusClass(status = "") {
  if (["paid", "assigned"].includes(status)) return "live";
  if (["partially_assigned", "pending_payment"].includes(status)) return "warn";
  if (["rejected", "cancelled"].includes(status)) return "danger";
  return "";
}

export default function MyPool() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [memberships, setMemberships] = useState([]);
  const [available, setAvailable] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [requesting, setRequesting] = useState(false);
  const [selectedPool, setSelectedPool] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  async function load() {
    setLoading(true);
    setError("");
    const [myResult, allResult, requestResult] = await Promise.allSettled([
      poolsApi.getMyPools(),
      poolsApi.getAll({ status: "filling", limit: 24 }),
      ticketRequestsApi.getMine(),
    ]);

    if (myResult.status === "fulfilled") {
      setMemberships(myResult.value.data.data || []);
    } else {
      setMemberships([]);
    }

    if (allResult.status === "fulfilled") {
      setAvailable(allResult.value.data.data || []);
    } else {
      setAvailable([]);
      setError(allResult.reason?.response?.data?.message || "Failed to load available pools.");
    }

    if (requestResult.status === "fulfilled") {
      setRequests(requestResult.value.data.data || []);
    } else {
      setRequests([]);
      if (allResult.status === "rejected") {
        setError(requestResult.reason?.response?.data?.message || "Failed to load pool and request data.");
      }
    }

    setLoading(false);
  }

  useEffect(() => { load(); }, []);

  useEffect(() => {
    const checkout = searchParams.get("checkout");
    const sessionId = searchParams.get("session_id");

    if (checkout === "cancelled") {
      setError("Checkout was cancelled. Your request is saved and can be paid when you are ready.");
      setSearchParams({}, { replace: true });
      return;
    }
    if (checkout !== "success" || !sessionId) return;

    setSuccess("Confirming your payment...");
    paymentsApi.getCheckoutStatus(sessionId)
      .then(({ data }) => {
        if (data.data.paymentStatus === "succeeded") {
          setSuccess(`Payment confirmed for ${data.data.requestId}. Admin can now assign your tickets.`);
          load();
        } else {
          setSuccess("Payment is still processing. This page will update when Stripe confirms it.");
        }
      })
      .catch((err) => setError(err.response?.data?.message || "Unable to confirm checkout status."))
      .finally(() => setSearchParams({}, { replace: true }));
  }, []);

  const openRequests = useMemo(
    () => requests.filter((r) => !["assigned", "rejected", "cancelled"].includes(r.status)),
    [requests]
  );

  function openRequest(pool) {
    setSelectedPool(pool);
    setQuantity(1);
    setError("");
    setSuccess("");
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!selectedPool) return;
    if (quantity < 1) {
      setError("Quantity must be at least 1 ticket.");
      return;
    }
    setRequesting(true);
    setError("");
    try {
      const { data } = await ticketRequestsApi.create({
        pool: selectedPool._id,
        quantity,
      });
      if (data.data.checkoutUrl) {
        window.location.assign(data.data.checkoutUrl);
        return;
      }
      setError(data.message || "Checkout is unavailable. Your pending request was saved.");
      setSelectedPool(null);
      await load();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to create ticket request.");
    } finally {
      setRequesting(false);
    }
  }

  async function handleResumeCheckout(requestId) {
    setRequesting(true);
    setError("");
    try {
      const { data } = await paymentsApi.resumeTicketRequestCheckout(requestId);
      if (data.data.url) {
        window.location.assign(data.data.url);
        return;
      }
      if (data.data.alreadyPaid) {
        setSuccess("Payment has already been confirmed.");
        await load();
      }
    } catch (err) {
      setError(err.response?.data?.message || "Unable to open Stripe checkout.");
    } finally {
      setRequesting(false);
    }
  }

  const requestColumns = [
    { name: "Request", selector: (row) => row.requestId || row._id || "", sortable: true, cell: (row) => <strong>{row.requestId || row._id?.slice(-6)}</strong> },
    { name: "Pool", selector: (row) => row.pool?.poolId || "", sortable: true, cell: (row) => <span>{row.pool?.poolId || "-"}<small className="table-muted">{row.pool?.name || ""}</small></span>, grow: 1.4 },
    { name: "Tickets", selector: (row) => row.quantity || 0, sortable: true },
    { name: "Paid", selector: (row) => row.totalAmount || 0, sortable: true, cell: (row) => <b className="table-primary">{fmt(row.totalAmount)}</b> },
    { name: "Assigned", selector: (row) => row.assignedTickets?.length || 0, sortable: true, cell: (row) => `${row.assignedTickets?.length || 0} / ${row.quantity}` },
    { name: "Status", selector: (row) => row.status || "", sortable: true, cell: (row) => <span className={`table-status ${statusClass(row.status)}`}>{row.status?.replaceAll("_", " ")}</span> },
    {
      name: "Payment",
      button: true,
      cell: (row) => row.status === "pending_payment" ? (
        <button className="table-action-btn" onClick={() => handleResumeCheckout(row._id)} disabled={requesting}>
          <CreditCard size={14} /> Pay
        </button>
      ) : <span className="table-muted">Complete</span>,
    },
  ];

  return (
    <div className="user-shell">
      <UserSideNav />
      <div className="user-app">
        <UserHeader />
        <main className="user-main">
          <div className="welcome-block">
            <div>
              <div className="page-kicker">Ticket Requests</div>
              <h1>Request Powerball Tickets</h1>
              <p>Select an open pool, choose ticket quantity, and send a paid request for admin to purchase and assign tickets on your behalf.</p>
            </div>
            <button className="hero-action" onClick={() => available[0] && openRequest(available[0])} disabled={!available.length}>
              <Plus size={18} /> New Request
            </button>
          </div>

          {error && <div className="auth-error">{error}</div>}
          {success && <div className="auth-success">{success}</div>}

          {loading ? (
            <div className="page-loading"><Loader2 size={28} className="spin" /><span>Loading pools...</span></div>
          ) : (
            <>
              <section className="summary-grid">
                <article className="workflow-stat"><FileClock size={20} /><span>Open Requests</span><strong>{openRequests.length}</strong></article>
                <article className="workflow-stat"><Ticket size={20} /><span>Requested Tickets</span><strong>{requests.reduce((sum, r) => sum + (r.quantity || 0), 0)}</strong></article>
                <article className="workflow-stat"><CheckCircle2 size={20} /><span>Assigned Tickets</span><strong>{requests.reduce((sum, r) => sum + (r.assignedTickets?.length || 0), 0)}</strong></article>
                <article className="workflow-stat"><Users size={20} /><span>Active Pools</span><strong>{memberships.length}</strong></article>
              </section>

              <section className="section-block">
                <div className="section-heading">
                  <h2><Plus size={20} /> Open Pools</h2>
                  <span className="muted-count">{available.length} available</span>
                </div>
                {available.length === 0 ? (
                  <div className="empty-state"><Trophy size={36} /><p>No open pools are accepting ticket requests right now.</p></div>
                ) : (
                  <div className="request-pool-grid">
                    {available.map((p) => {
                      const pct = p.size ? Math.min((p.memberCount / p.size) * 100, 100) : 0;
                      return (
                        <article key={p._id} className="request-pool-card">
                          <div className="request-card-top">
                            <div>
                              <code>{p.poolId}</code>
                              <h3>{p.name}</h3>
                            </div>
                            <span className="status-pill live">Open</span>
                          </div>
                          <div className="request-card-meta">
                            <span><CalendarDays size={14} /> {fmtDate(p.drawDate)}</span>
                            <span><DollarSign size={14} /> {fmt(p.entryAmount)} per ticket</span>
                            <span><Trophy size={14} /> {p.jackpotEstimate ? fmt(p.jackpotEstimate) : "Jackpot TBA"}</span>
                          </div>
                          <div>
                            <div className="request-fill-row"><span>{p.memberCount} / {p.size} pool load</span><b>{pct.toFixed(0)}%</b></div>
                            <span className="progress"><i style={{ width: `${pct}%` }} /></span>
                          </div>
                          <button className="pool-join-btn" onClick={() => openRequest(p)}>
                            <CreditCard size={15} /> Request Tickets
                          </button>
                        </article>
                      );
                    })}
                  </div>
                )}
              </section>

              <section className="section-block">
                <div className="section-heading">
                  <h2><FileClock size={20} /> My Ticket Requests</h2>
                  <span className="muted-count">{requests.length} total</span>
                </div>
                <SmartDataTable
                  columns={requestColumns}
                  data={requests}
                  loading={false}
                  searchFields={["requestId", "pool.poolId", "pool.name", "status"]}
                  searchPlaceholder="Search requests by pool, status, request ID..."
                  emptyMessage="No ticket requests yet. Choose an open pool above to start."
                  paginationPerPage={25}
                />
              </section>
            </>
          )}
        </main>
      </div>

      <Modal open={!!selectedPool} onClose={() => setSelectedPool(null)} title="Request Powerball Tickets" subtitle={selectedPool ? `${selectedPool.poolId} - ${selectedPool.name}` : ""} icon={<Ticket size={18} />} size="sm">
        <form className="modal-form" onSubmit={handleSubmit}>
          {error && <div className="auth-error">{error}</div>}
          <div className="request-confirm-card">
            <span>Ticket price</span>
            <strong>{fmt(selectedPool?.entryAmount)}</strong>
            <small>Payment request is recorded first. Admin purchases and assigns ticket numbers manually.</small>
          </div>
          <div className="auth-field">
            <label>Number of tickets</label>
            <input type="number" min={1} value={quantity} onChange={(e) => setQuantity(Number(e.target.value))} autoFocus />
          </div>
          <div className="request-total">
            <span>Total due</span>
            <strong>{fmt((selectedPool?.entryAmount || 0) * quantity)}</strong>
          </div>
          <div className="modal-actions">
            <button type="button" className="modal-btn-cancel" onClick={() => setSelectedPool(null)}>Cancel</button>
            <button type="submit" className="modal-btn-submit" disabled={requesting}>
              {requesting ? <><Loader2 size={14} className="spin" /> Sending...</> : <><CreditCard size={14} /> Pay & Request</>}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
